import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChannelGroupTabs } from '../ChannelGroupForm/ChannelGroupFormModel';
import { BreadcrumbsRoute } from 'use-react-router-breadcrumbs';
import { DynamicBreadcrumb } from 'components/Breadcrumbs/DynamicBreadcrumbs';
import i18n from 'i18n';
import { ChannelGroupDTO } from 'core/channelGroup/ChannelGroup';
import { useCallAPI } from 'hooks/useCallAPI';
import {
  ChannelGroupManager,
  DefaultChannelGroupManager
} from 'core/channelGroup/ChannelGroupManager';
import { useParams } from 'react-router-dom';
import { AgencyManager, DefaultAgencyManager } from 'core';
import { ChannelGroupAuditLogModel } from 'containers/AuditLog/ChannelGroupAuditLogModel';

/**
 * ECOPD-5572：渠道管理畫面上的自停 / 解除自停動作。
 * 端點語彙是 archive / unarchive，**UI 上的中文字是「停用」/「重新啟用」**。
 */
export type ChannelGroupArchiveAction = 'archive' | 'unarchive';

export type ChannelGroupDetailModelData = {
  loading: boolean;
  tab: ChannelGroupTabs;
  channelGroup?: ChannelGroupDTO;
  agencyOptions: SelectOptions[];
  breadcrumbsRoutes: BreadcrumbsRoute[];
  onTabChange: (tab: string | null) => void;
  /** 有值時代表確認框開著，等使用者確認這個動作。 */
  pendingArchiveAction?: ChannelGroupArchiveAction;
  requestArchive: () => void;
  requestUnarchive: () => void;
  /** ECOPD-5701：`reason` 只有 archive 用得到；unarchive 端點沒有 body。 */
  confirmArchiveAction: (reason?: string) => void;
  cancelArchiveAction: () => void;
  /** ECOPD-5701：操作歷程紀錄 tab 的 model。以渠道 id 為 key 快取。 */
  auditLogModel: ChannelGroupAuditLogModel;
};

const channelGroupManager: ChannelGroupManager =
  new DefaultChannelGroupManager();
const agencyManager: AgencyManager = new DefaultAgencyManager();

export const useChannelGroupDetailModel = (
  /**
   * ECOPD-5572：停用 / 重新啟用成功後通知外層重抓渠道清單。
   * 渠道管理列表讀的是 DTO 而不是 registry，所以 manager 那邊就地更新 registry
   * 救不到它 —— 不呼叫這支，使用者走回列表會看到停用前的舊資料。
   */
  onChannelGroupChanged?: () => void
): ChannelGroupDetailModelData => {
  const params = useParams<{ id: string }>();
  const id = +params.id;
  const [tab, setTab] = useState(ChannelGroupTabs.BASIC);
  const [channelGroup, setChannelGroup] = useState<ChannelGroupDTO>();
  const [agencyOptions, setAgencyOptions] = useState<SelectOptions[]>([]);

  const { loading, callAPIs } = useCallAPI();

  useEffect(() => {
    callAPIs(
      [
        () => channelGroupManager.getChannelGroup(id),
        () => agencyManager.getAgenciesOptions()
      ],
      (channelGroup: ChannelGroupDTO, agencyOptions: SelectOptions[]) => {
        setChannelGroup(channelGroup);
        setAgencyOptions(agencyOptions);
      }
    );
  }, [id, callAPIs]);

  const [pendingArchiveAction, setPendingArchiveAction] =
    useState<ChannelGroupArchiveAction>();

  const requestArchive = useCallback(
    () => setPendingArchiveAction('archive'),
    []
  );
  const requestUnarchive = useCallback(
    () => setPendingArchiveAction('unarchive'),
    []
  );
  const cancelArchiveAction = useCallback(
    () => setPendingArchiveAction(undefined),
    []
  );

  /**
   * ECOPD-5572：確認後才真的送出。
   *
   * 停用是**不可逆**的：後端會暫停底下啟用中的活動、停用 PMP 訂單，並且刪除 RMax 端
   * 對應的 deal，而解除自停不會恢復任何一項。所以入口一律經過確認框，
   * 而確認框的文案必須把這三件事講明白。
   *
   * 成功後直接把回傳的渠道寫進本地 state —— manager 那層已經順手更新了
   * `ChannelGroupSetting`，全站的判定與名稱後綴不需要重新整理頁面。
   * 成功後**停留在這一頁**，不導回列表：狀態與停用理由就地更新，按鈕變「重新啟用」。
   *
   * ECOPD-5701：`reason` 送出前 trim（後端對空字串與純空白回 400，表單那層已先擋）。
   */
  const confirmArchiveAction = useCallback(
    (reason?: string) => {
      if (!pendingArchiveAction) {
        return;
      }
      const action = pendingArchiveAction;
      callAPIs(
        [
          () =>
            action === 'archive'
              ? channelGroupManager.archiveChannelGroup(
                  id,
                  (reason ?? '').trim()
                )
              : channelGroupManager.unarchiveChannelGroup(id)
        ],
        (updated: ChannelGroupDTO) => {
          // ECOPD-5701：確認框**只在成功後**才關。原本在送出前就關，請求失敗時
          // 使用者剛打完的 500 字理由會跟著消失。錯誤由 toast 呈現，框留著可直接重送。
          setPendingArchiveAction(undefined);
          setChannelGroup(updated);
          onChannelGroupChanged?.();
        }
      );
    },
    [pendingArchiveAction, id, callAPIs, onChannelGroupChanged]
  );

  // 以渠道 id 為 key 的 lazy cache。這裡**不**預先 init —— `<AuditLog>` 的
  // componentDidMount 沒資料時會自己 init，所以第一次切到該 tab 才發請求；
  // 切走 unmount、切回來因為同一個實例已有資料不會重抓。
  const auditLogModel = useMemo(() => new ChannelGroupAuditLogModel(id), [id]);

  const onTabChange = useCallback((tab: string | null) => {
    if (!tab) {
      return;
    }
    setTab(tab as ChannelGroupTabs);
  }, []);

  const breadcrumbsRoutes = useMemo(
    () => [
      {
        path: '/system/channel-groups',
        breadcrumb: i18n.t('channelGroups.title')
      },
      {
        path: '/system/channel-groups/:id',
        breadcrumb: DynamicBreadcrumb,
        props: {
          label: channelGroup ? channelGroup.name : '',
          matchParam: 'id'
        }
      }
    ],
    [channelGroup]
  );

  return {
    loading,
    tab,
    channelGroup,
    agencyOptions,
    breadcrumbsRoutes,
    onTabChange,
    pendingArchiveAction,
    requestArchive,
    requestUnarchive,
    confirmArchiveAction,
    cancelArchiveAction,
    auditLogModel
  };
};
